import fp from 'lodash/fp';
import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link as RRLink, useLocation } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';

import {
    Link,
    Button,
    cn,
} from 'Modules/ui2';
import { useConfig } from 'Modules/core/config';
import { Desktop, useDesktop, useMediaSwitch, Stack } from 'Modules/ui3';

import { GlobalStyle } from '../ui/styles';
import { DFALogo, Menu, Close } from '../../icons';
import { headerRoutes, routes } from '../../routes/routes';
import { useScrollPosition } from './scroll-position';
import styles from './index.module.scss';
import { ThemeProvider, themeLight } from '../ui/theme';
import { Footer } from '../Footer';
import { Header, HeaderProvider } from '../Header';
import { FooterOld } from '../FooterOld';


const scrollWithOffset = (el, offset) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = offset;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
};

const HeaderOld = React.memo(({ headerRef }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname, hash } = useLocation();
    useEffect(
        () => setIsOpen(false),
        [pathname, hash],
    );
    useLayoutEffect(
        () => {
            document.body.style.overflow = isOpen ? 'hidden' : '';
            return () => { document.body.style.overflow = ''; };
        },
        [isOpen],
    );

    const lkflUrl = useConfig('lkflUrl');
    const showAuthorization = useConfig('showAuthorization');

    const scrollOffset = useMediaSwitch(-112, -76, -60);
    const scrollCallback = useCallback(
        el => scrollWithOffset(el, scrollOffset),
        [scrollOffset],
    );
    const isDesktop = useDesktop();

    return (
        <nav className={cn(styles.header, isOpen && styles.menuIsOpen)} ref={headerRef}>
            <Desktop>
                <RRLink to='/' className={styles.logoLink}>
                    <DFALogo className={styles.logo} />
                </RRLink>
            </Desktop>
            <div className={cn(styles.links, isOpen && styles.isOpen)}>
                {headerRoutes.map(key => routes.home.anchors[key]).map(({ route, name }) => (
                    <Link
                        key={route}
                        component={NavHashLink}
                        className={styles.headerLink}
                        innerClassName={styles.linkContent}
                        to={`/${route}`}
                        exact
                        smooth
                        scroll={scrollCallback}
                    >
                        {name}
                    </Link>
                ))}
            </div>
            {showAuthorization && isDesktop && (
                <div className={styles.coRegistration__wrapper}>
                    <div className={styles.coRegistration}>
                        <Link
                            className={styles.headerLink}
                            innerClassName={styles.linkContent}
                            href={lkflUrl}
                        >
                            Вход
                        </Link>
                    </div>
                </div>
            )}
            {!isDesktop && (
                <>
                    <RRLink to='/' className={styles.logoLink}>
                        <DFALogo className={styles.logo} />
                    </RRLink>
                    <Button className={styles.mobileMenu} onClick={() => setIsOpen(!isOpen)}>
                        <Menu className={!isOpen ? styles.active : ''} />
                        <Close className={cn(styles.close, isOpen ? styles.active : '')} />
                    </Button>
                </>
            )}
        </nav>
    );
});

const useScrollWatcher = ref => {
    const [scrollHidden, setScrollHidden] = useState(false);
    const callback = useMemo(
        () => {
            let scrollHidden = false;
            let lastScroll = 0;
            const callback = scroll => {
                const height = fp.get('current.offsetHeight', ref);
                const delta = lastScroll - scroll;
                if (scrollHidden && delta > height) {
                    lastScroll = scroll;
                    setScrollHidden(scrollHidden = false);
                }
                if (!scrollHidden && delta < -height) {
                    lastScroll = scroll;
                    setScrollHidden(scrollHidden = true);
                }
                if (scrollHidden && delta < 0 || !scrollHidden && delta > 0)
                    lastScroll = scroll;
            };
            return callback;
        },
        [ref],
    );
    return { scrollHidden, callback };
};

/** @type {React.FC<{ overlay?: boolean, isHeaderLocked?: boolean }>} */
export const LandingLayout = ({
    children,
    overlay = false,
    isHeaderLocked = true,
}) => {
    const { pathname } = useLocation();
    useLayoutEffect(
        () => { window.scroll(0, 0); },
        [pathname],
    );

    const headerRef = useRef();
    const headerHeight = fp.get('current.offsetHeight', headerRef);
    const { scrollHidden, callback } = useScrollWatcher(headerRef);
    const scroll = useScrollPosition(callback);

    const [headerLocked, setHeaderLocked] = useState(isHeaderLocked);
    useEffect(
        () => {
            if (typeof isHeaderLocked !== 'undefined')
                setHeaderLocked(isHeaderLocked);
            else if (scroll > headerHeight)
                setHeaderLocked(true);
            else if (scroll < 1)
                setHeaderLocked(false);
        },
        [scroll, headerHeight, isHeaderLocked],
    );

    const [headerOverlay, setHeaderOverlay] = useState(overlay);
    useEffect(() => {
        setHeaderOverlay(overlay);
    }, [overlay, pathname]);

    const className = cn(
        styles.landing,
        headerOverlay && styles.overlay,
        headerLocked && styles.fixedHeader,
        (isHeaderLocked || !scrollHidden) && styles.showHeader,
        headerOverlay && !isHeaderLocked &&
        (scroll < headerHeight / 2 || !headerLocked) && styles.transparentHeader,
    );
    return (
        <>
            {pathname === '/' ? (
                <HeaderProvider>
                    <Stack alignItems='center' sx={{ overflowX: 'hidden' }}>
                        <Header />
                        {children}
                        <Footer />
                    </Stack>
                </HeaderProvider>
            ) : (
                <ThemeProvider theme={themeLight}>
                    <GlobalStyle />
                    <div className={className}>
                        <HeaderOld key={headerLocked || !scrollHidden} headerRef={headerRef} />
                        {children}
                    </div>
                    <FooterOld />
                </ThemeProvider>
            )}
        </>
    );
};
